import type { ReactNode } from 'react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { withMask } from 'use-mask-input';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { cn } from '@/lib/utils';
import { type ColSpan, lgSpan, mdSpan, smSpan } from './form-input';

type FormMaskedInputProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  placeholder?: string;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  autoComplete?: string;

  sm?: ColSpan;
  md?: ColSpan;
  lg?: ColSpan;

  className?: string;

  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  mask: string | string[];
} & React.ComponentProps<'input'>;

export function FormMaskedInput<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  type = 'text',
  autoComplete,
  leftAddon,
  rightAddon,
  sm = 12,
  md = 4,
  lg = 4,
  mask,
  className,
  ...props
}: FormMaskedInputProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field
          className={cn(sm && smSpan[sm], md && mdSpan[md], lg && lgSpan[lg], className)}
          aria-required
          data-invalid={fieldState.invalid}
        >
          <FieldLabel htmlFor={name}>
            {label}
            {props?.required && <strong className="text-red-600">*</strong>}
          </FieldLabel>

          <InputGroup>
            <InputGroupInput
              {...field}
              {...props}
              value={field.value ?? ''}
              id={name}
              placeholder={placeholder}
              type={type ?? 'text'}
              autoComplete={autoComplete}
              aria-invalid={fieldState.invalid}
              ref={withMask(mask, { removeMaskOnSubmit: true })}
            />

            {leftAddon && (
              <InputGroupAddon align="inline-start">{leftAddon}</InputGroupAddon>
            )}
            {rightAddon && (
              <InputGroupAddon align="inline-end">{rightAddon}</InputGroupAddon>
            )}
          </InputGroup>

          {fieldState.error && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
